import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { BASE_URL } from '../../config/env';
import { removeEmpty, buildQueryParams } from '../../utils/object';

export const farmApi = createApi({
  reducerPath: 'farmApi',
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = getState()?.auth?.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Farm', 'FieldLocation', 'Observations', 'FarmHistory'],
  endpoints: (builder) => ({
    getFarms: builder.query({
      query: (params = {}) => {
        const {
          page,
          limit,
          search,
          farmer_name,
          crop_id,
          state_id,
          district_id,
          sub_district_id,
          village_id,
          status,
          client_id,
          project_id,
        } = params;
        return {
          url: 'farm',
          params: buildQueryParams({
            page,
            limit,
            search,
            farmer_name,
            crop_id,
            state_id,
            district_id,
            sub_district_id,
            village_id,
            status,
            client_id,
            project_id,
          }),
        };
      },
      providesTags: ['Farm'],
    }),
    getFarmDetails: builder.query({
      query: ({ farmId }) => ({
        url: `farm/details/${farmId}`,
      }),
      providesTags: ['Farm'],
    }),
    updateFarm: builder.mutation({
      query: ({ farmerId, farmId, payload }) => ({
        url: `farm/update/${farmerId}/${farmId}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: ['Farm'],
    }),
    updateFarmStatus: builder.mutation({
      query: ({ farmerId, farmId, status, reason }) => ({
        url: `farm/${farmerId}/${farmId}/status`,
        method: 'PATCH',
        body: { status, reason },
      }),
      invalidatesTags: ['Farm'],
    }),
    deleteFarm: builder.mutation({
      query: ({ farmerId, farmId }) => ({
        url: `farm/${farmerId}/${farmId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Farm'],
    }),
    getObservation: builder.query({
      query: ({ entityId, type = 'farm', observationId }) => ({
        url: `observations/${entityId}`,
        params: buildQueryParams({ type, observation_id: observationId }),
      }),
      providesTags: ['Observations'],
    }),
    getObservationsList: builder.query({
      query: (params = {}) => {
        const {
          page,
          limit,
          search,
          crop_id,
          client_id,
          type,
          from_date,
          to_date,
        } = params;
        return {
          url: 'observations/list',
          params: buildQueryParams({
            page,
            limit,
            search,
            crop_id,
            client_id,
            type,
            from_date,
            to_date,
          }),
        };
      },
      providesTags: ['Observations'],
    }),
    getFarmMap: builder.query({
      query: (filters) => {
        const params = removeEmpty(filters);

        return {
          url: 'farm/map',
          ...(Object.keys(params).length > 0 && { params: params }),
        };
      },
      providesTags: ['Farm'],
    }),
    getFarmHistory: builder.query({
      query: (params) => {
        const { farm_id, page, limit, date } = params || {};
        const queryParams = removeEmpty({
          page,
          limit,
          date,
        });

        return {
          url: `farm/history/${farm_id}`,
          ...(Object.keys(queryParams).length > 0 && { params: queryParams }),
        };
      },
      providesTags: ['FarmHistory'],
    }),
    downloadObservation: builder.mutation({
      query: ({ entityId, observationId, type = 'farm' }) => ({
        url: `observations/${entityId}/download`,
        method: 'GET',
        params: buildQueryParams({ type, observation_id: observationId }),
        responseHandler: (response) => response.blob(),
      }),
    }),
    getFieldLocations: builder.query({
      query: (params = {}) => ({
        url: 'field-locations',
        params: removeEmpty(params),
      }),
      providesTags: ['FieldLocation'],
    }),
    getFarmFilterOptions: builder.query({
      query: ({ filter, ...args }) => {
        const params = removeEmpty(args);
        return {
          url: `farm/filters/${filter}`,
          ...(Object.keys(params).length > 0 && { params }),
        };
      },
      transformResponse: (response) => response?.data || [],
      providesTags: ['Farm'],
    }),
    getFieldFilterOptions: builder.query({
      query: ({ filter, ...args }) => {
        const params = removeEmpty(args);
        return {
          url: `field-locations/filters/${filter}`,
          ...(Object.keys(params).length > 0 && { params }),
        };
      },
      transformResponse: (response) => response?.data || [],
      providesTags: ['FieldLocation'],
    }),
  }),
});

export const {
  useGetFarmsQuery,
  useGetFarmDetailsQuery,
  useUpdateFarmMutation,
  useUpdateFarmStatusMutation,
  useDeleteFarmMutation,
  useGetObservationQuery,
  useGetObservationsListQuery,
  useGetFarmMapQuery,
  useGetFarmHistoryQuery,
  useDownloadObservationMutation,
  useGetFieldLocationsQuery,
  useGetFarmFilterOptionsQuery,
  useGetFieldFilterOptionsQuery,
} = farmApi;
